import { FileSourceEnum } from '@systemeio/file-manager';
import { FormInput, FormInputPhone, FormSelect, Hint, Popover, RadioGroup, RadioGroupDataInterface, useLocalizedCountries, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { updateUserSettings } from 'modules/profile/user-settings/api/user-settings-api';
import UserSettingsEmail from 'modules/profile/user-settings/components/user-settings-email';
import UserSettingsSkeleton from 'modules/profile/user-settings/components/user-settings-skeleton';
import { UserSettingsLoco } from 'modules/profile/user-settings/constants/user-settings-loco';
import { CustomerTypeEnum } from 'modules/profile/user-settings/enums/customer-type-enum';
import { useUserSettings } from 'modules/profile/user-settings/hooks/use-user-settings';
import { EditableDataUserSettings, EditableUserSettings, UserSettingsErrorResponseContentInterface, UserSettingsErrorsInterface } from 'modules/profile/user-settings/types/user-settings-interface';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import { AvatarUploader } from 'shared/components/file-uploader/avatar-uploader';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { UserInterface } from 'shared/types/user-interface';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { InfoIcon, QuestionMarkIcon } from '@systemeio/ui/icons';
import { hasSubAccounts } from 'shared/hooks/use-user/utils/has-subaccounts';
const defaultErrors: UserSettingsErrorsInterface = {
  taxNumber: '',
  address: '',
  city: '',
  companyName: '',
  country: '',
  customerType: '',
  firstName: '',
  phone: '',
  state: '',
  lastName: '',
  postCode: '',
  companyNumber: '',
  displayName: '',
  profileImage: ''
};
const UserSettings: NextPage = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    mutate: userDataMutate,
    isAffiliate,
    isCustomer,
    isSubAccountImpersonation,
    avatarUrl
  } = useUser();
  const {
    userSettings,
    mutate
  } = useUserSettings();
  const {
    countryData
  } = useLocalizedCountries();
  const customerTypeData: RadioGroupDataInterface<EditableUserSettings['customerType']>[] = [{
    id: 'personal',
    caption: t(CustomerTypeEnum.personal)
  }, {
    id: 'company',
    caption: t(CustomerTypeEnum.company)
  }];
  const [tempState, setTempState] = useState<EditableUserSettings | undefined>();
  const [errors, setErrors] = useState<UserSettingsErrorsInterface>(defaultErrors);
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (userSettings && userSettings.user) {
      setTempState({
        companyNumber: userSettings.companyNumber,
        ...userSettings.user,
        displayName: userSettings.userDomainAccount.displayName,
        profileImage: userSettings.userDomainAccount.profileImage
      });
    }
  }, [userSettings]);
  const updateProfile = async () => {
    if (!tempState) return;
    try {
      const isCompany = tempState.customerType === 'company';
      const data: EditableDataUserSettings = {
        companyNumber: isCompany ? tempState.companyNumber : null,
        user: {
          address: tempState.address,
          city: tempState.city,
          companyName: isCompany ? tempState.companyName : null,
          country: tempState.country || null,
          firstName: tempState.firstName,
          lastName: tempState.lastName,
          state: tempState.state,
          phone: tempState.phone,
          customerType: tempState.customerType,
          postCode: tempState.postCode,
          taxNumber: isCompany ? tempState.taxNumber : null
        },
        userDomainAccount: {
          displayName: tempState.displayName,
          profileImage: tempState.profileImage?.id || null
        }
      };
      setIsFetching(true);
      const response = await updateUserSettings(data);
      await mutate(response.data, false);
      const profileImageData = response.data.userDomainAccount.profileImage;
      const displayName = response.data.userDomainAccount.displayName;
      await userDataMutate(data_0 => {
        if (!data_0) return data_0;
        const updatedData: UserInterface = {
          ...data_0
        };
        if (response.data.user) {
          updatedData.firstName = response.data.user.firstName ?? updatedData.firstName;
          updatedData.lastName = response.data.user.lastName ?? updatedData.lastName;
          updatedData.country = response.data.user.country ?? updatedData.country;
        }
        const userId = isSubAccountImpersonation ? data_0.id : data_0.mainUser?.user.id;
        if (isSubAccountImpersonation && hasSubAccounts(data_0)) {
          updatedData.subaccounts = data_0.subaccounts.map(subAccount => subAccount.user.id === data_0.id ? {
            ...subAccount,
            profileImage: profileImageData ?? subAccount.profileImage,
            displayName: displayName ?? subAccount.displayName
          } : subAccount);
        } else if (data_0.mainUser) {
          updatedData.mainUser = {
            ...data_0.mainUser,
            profileImage: profileImageData ?? data_0.mainUser.profileImage,
            displayName: displayName ?? data_0.mainUser.displayName
          };
        }
        if (profileImageData?.path && userId) {
          updatedData.subaccountsAvatarUrls = {
            ...data_0.subaccountsAvatarUrls,
            [userId]: profileImageData.path
          };
        }
        return updatedData;
      }, false);
      setIsFetching(false);
      toast.success(t('global.changes_saved'));
      setErrors(defaultErrors);
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const userSettingErrors = e.errors as unknown as UserSettingsErrorResponseContentInterface;
        setErrors({
          firstName: userSettingErrors.fields.user?.firstName?.join(''),
          lastName: userSettingErrors.fields.user?.lastName?.join(''),
          phone: userSettingErrors.fields.user?.phone?.join(''),
          address: userSettingErrors.fields.user?.address?.join(''),
          city: userSettingErrors.fields.user?.city?.join(''),
          country: userSettingErrors.fields.user?.country?.join(''),
          postCode: userSettingErrors.fields.user?.postCode?.join(''),
          state: userSettingErrors.fields.user?.state?.join(''),
          taxNumber: userSettingErrors.fields.user?.taxNumber?.join(''),
          companyName: userSettingErrors.fields.user?.companyName?.join(''),
          customerType: userSettingErrors.fields.user?.customerType?.join(''),
          companyNumber: userSettingErrors.fields.user?.companyNumber?.join(''),
          displayName: userSettingErrors.fields.userDomainAccount?.displayName?.join(''),
          profileImage: userSettingErrors.fields.userDomainAccount?.profileImage?.join('')
        });
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  return <SettingsLayout titles={[{
    caption: t(settingsMenuLocoKeys.profile),
    isStatic: true
  }]} onSave={updateProfile} isFetching={isFetching} disabled={!userSettings}>
      {tempState && userSettings ? <div className="bg-background-secondary flex h-fit w-full flex-col gap-5 overflow-x-hidden rounded-md">
          <UserSettingsEmail email={userSettings.user?.email} mutate={mutate}>
            {(isAffiliate || isCustomer) && <p className={'flex items-center'}>
                <InfoIcon className={'fill-brand-primary mr-2 inline'} />

                {t('dashboard.user.profile.data_used_in_invoice_hint')}
              </p>}
          </UserSettingsEmail>
          <div className={'flex flex-col items-start gap-1 px-5'}>
            <Hint label={t('dashboard.user.profile_image_hint')}>
              <span className={'text-text-primary text-base font-bold'}>
                {t('dashboard.user.profile_image')}
              </span>
            </Hint>
            <AvatarUploader source={FileSourceEnum.Settings} onSetPhoto={dataFile => setTempState(prev => prev && {
          ...prev,
          profileImage: dataFile
        })} fileManagerApiType={{
          isPublic: isAffiliate && !isCustomer
        }}>
              <img className={'border-brand-primary rounded-full border-2 object-cover '} alt={userSettings.user?.email} width={128} height={128} src={tempState?.profileImage?.path || avatarUrl} />
            </AvatarUploader>
          </div>
          <div className="flex flex-col gap-7 px-5 pb-5 lg:gap-10">
            <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
              <FormInput placeholder={t('dashboard.user.display_name')} label={<div className={'flex items-center gap-1.5'}>
                    {t('dashboard.user.display_name')}
                    <Popover label={t('dashboard.user.display_name-info')} popoverClassName={'w-[200px] sm:w-[350px]'}>
                      {show => <QuestionMarkIcon className={`${show ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                    </Popover>
                  </div>} required value={tempState.displayName || ''} onChange={e_0 => setTempState(prev_0 => prev_0 && {
            ...prev_0,
            displayName: e_0.target.value
          })} error={errors.displayName} />
              <FormInput placeholder={'John'} label={t('dashboard.user.first_name')} value={tempState.firstName || ''} onChange={e_1 => setTempState(prev_1 => prev_1 && {
            ...prev_1,
            firstName: e_1.target.value
          })} error={errors.firstName} />
              <FormInput placeholder={'Doe'} label={t('dashboard.user.last_name')} value={tempState.lastName || ''} onChange={e_2 => setTempState(prev_2 => prev_2 && {
            ...prev_2,
            lastName: e_2.target.value
          })} error={errors.lastName} />
            </div>
            <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
              <FormSelect placeholder={t('dashboard.user.select_country')} data={countryData} label={t('dashboard.user.country')} value={tempState.country || undefined} onChange={value => setTempState(prev_3 => prev_3 && {
            ...prev_3,
            country: value ?? null
          })} error={errors.country} />
              <FormInput placeholder={'San Francisco'} label={t('dashboard.user.city')} value={tempState.city || ''} onChange={e_3 => setTempState(prev_4 => prev_4 && {
            ...prev_4,
            city: e_3.target.value
          })} error={errors.city} />
              <FormInput placeholder={t('dashboard.user.street_address')} label={t('dashboard.user.address')} value={tempState.address || ''} onChange={e_4 => setTempState(prev_5 => prev_5 && {
            ...prev_5,
            address: e_4.target.value
          })} error={errors.address} />
            </div>
            <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
              <FormInput placeholder={t('dashboard.user.postal_code')} label={t('dashboard.user.postal_code')} value={tempState.postCode || ''} onChange={e_5 => setTempState(prev_6 => prev_6 && {
            ...prev_6,
            postCode: e_5.target.value
          })} error={errors.postCode} />
              <FormInput placeholder={'California'} label={t('dashboard.user.state_province')} value={tempState.state || ''} onChange={e_6 => setTempState(prev_7 => prev_7 && {
            ...prev_7,
            state: e_6.target.value
          })} error={errors.state} />
              <FormInputPhone label={t('dashboard.user.phone_number')} onChange={phone => setTempState(prev_8 => prev_8 && {
            ...prev_8,
            phone: phone
          })} value={tempState.phone || ''} error={errors.phone} />
            </div>
            <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
              <RadioGroup label={t('dashboard.user.customer_type')} selected={tempState.customerType} options={customerTypeData} onChange={value_0 => setTempState(prev_9 => prev_9 && {
            ...prev_9,
            customerType: value_0
          })} error={errors.customerType} />
            </div>
            {tempState.customerType === 'company' && <div className="flex flex-col gap-7 lg:gap-10">
                <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                  <FormInput placeholder={'Amazon'} label={t('dashboard.user.company_name')} required value={tempState.companyName || ''} onChange={e_7 => setTempState(prev_10 => prev_10 && {
              ...prev_10,
              companyName: e_7.target.value
            })} error={errors.companyName} />
                  <FormInput placeholder={t('dashboard.user.company_number')} label={t('dashboard.user.company_number')} value={tempState.companyNumber || ''} onChange={e_8 => setTempState(prev_11 => prev_11 && {
              ...prev_11,
              companyNumber: e_8.target.value
            })} error={errors.companyNumber} />
                  <FormInput placeholder={t('dashboard.user.tax_number')} label={t('dashboard.user.tax_number')} value={tempState.taxNumber || ''} onChange={e_9 => setTempState(prev_12 => prev_12 && {
              ...prev_12,
              taxNumber: e_9.target.value
            })} error={errors.taxNumber} />
                </div>
              </div>}
          </div>
        </div> : <UserSettingsSkeleton />}
    </SettingsLayout>;
};
export const getServerSideProps = getBaseServerSideProps(UserSettingsLoco, 'global.settings');
export default UserSettings;